import axios from 'axios';

const root = '/api/credit/abf/forward-flow/programs';
const base = (program: number) => `${root}/${program}`;

export type StopEvent = {
  id: number; kind: 'CONTRACT_STOP' | 'INTERNAL_HOLD'; trigger_ref: string; reason: string;
  scope: 'NEW_DECISIONS' | 'RELEASE' | 'BOTH'; accepted_treatment: 'CONTINUE' | 'STOP_SETTLEMENT';
  measurement_date: string; notice_date?: string | null; cure_deadline?: string | null; effective_date: string;
  source_ref?: string | null; source_sha256?: string | null; state: 'DRAFT' | 'ACTIVE' | 'RESUMED' | 'VOID';
  declared_by: string; declared_at: string; verified_by?: string | null;
  resume_reason?: string | null; resume_prepared_by?: string | null; resumed_by?: string | null; resumed_at?: string | null;
};

export type Claim = {
  id: number; loan_id: number; source_loan_id: string; warranty_ref: string; breach_summary: string;
  discovery_date: string; notice_deadline?: string | null; cure_deadline?: string | null;
  financial_estimate?: number | null; materiality_note?: string | null; seller_response?: string | null;
  state: string; outcome?: string | null; created_by: string; created_at: string; updated_at: string;
  repurchase_id?: number | null; repurchase_state?: string | null; agreed_amount?: number | null;
  notice_overdue?: boolean;
};
export type ClaimDetail = Claim & {
  events: Array<{action: string; detail: string; source_ref?: string | null; actor: string; happened_at: string}>;
  repurchases: Repurchase[];
};

export type Repurchase = {
  id: number; claim_id: number; loan_id: number; source_loan_id?: string; state: string;
  upb_at_pricing: number; accrued_at_pricing: number; price_per_100: number;
  principal_component: number; accrued_component: number; fee_component: number; offset_component: number;
  agreed_amount: number; effective_date?: string | null; trade_id?: number | null;
  receivable_total?: number | null; receivable_remaining?: number | null;
  prepared_by: string; approved_by?: string | null; computation?: string; trade_status?: string | null;
};

export type RemediationImport = {
  id: number; source_loan_id: string; actual_trade_date: string; actual_transfer_date: string;
  actual_cash_date?: string | null; actual_price_per_100: number; acquired_upb: number;
  principal_consideration: number; purchased_interest: number; paid_amount?: number | null;
  bank_reference?: string | null; authority_gap: string; source_ref: string; state: 'DRAFT' | 'RECORDED' | 'VOID';
  loan_id?: number | null; trade_id?: number | null; acquisition_id?: number | null;
  prepared_by: string; prepared_at: string; recorded_by?: string | null; recorded_at?: string | null;
};

export type ClosureChecklist = {
  programStatus: string; openLoans: number; openUpb: number; unpaidAcquisitions: number; openClaims: number;
  openRepurchaseReceivable: number; openServicingPeriods: number; openWorkItems: number; activeStopEvents: number;
};

export type AmendmentImpact = {
  proposed: {id: number; versionNumber: number; status: string; effectiveFrom?: string | null};
  active: {id: number; versionNumber: number; status: string; effectiveFrom?: string | null} | null;
  changes: Array<{term: string; from: string; to: string; material: boolean}>;
  materialChange?: boolean;
  openBatches?: Array<{id: number; state: string; seller_reference: string; revision_number: number;
    governing_agreement_id: number; decision_id?: number | null; decision_state?: string | null}>;
  openObligations?: Array<{acquisition_id: number; state: string; payable_remaining: number;
    governing_agreement_id: number}>;
  note?: string;
};

export const remedyApi = {
  stopEvents: async (program: number) => (await axios.get<StopEvent[]>(`${base(program)}/stop-events`)).data,
  declareStop: async (program: number, body: Record<string, unknown>) =>
    (await axios.post(`${base(program)}/stop-events`, body)).data,
  verifyStop: async (program: number, stop: number) =>
    (await axios.post(`${base(program)}/stop-events/${stop}/verify`)).data,
  prepareResume: async (program: number, stop: number, body: Record<string, unknown>) =>
    (await axios.post(`${base(program)}/stop-events/${stop}/resume`, body)).data,
  verifyResume: async (program: number, stop: number) =>
    (await axios.post(`${base(program)}/stop-events/${stop}/resume/verify`)).data,
  voidStop: async (program: number, stop: number) =>
    (await axios.post(`${base(program)}/stop-events/${stop}/void`)).data,

  claims: async (program: number) => (await axios.get<Claim[]>(`${base(program)}/claims`)).data,
  claim: async (program: number, claim: number) =>
    (await axios.get<ClaimDetail>(`${base(program)}/claims/${claim}`)).data,
  createClaim: async (program: number, body: Record<string, unknown>) =>
    (await axios.post(`${base(program)}/claims`, body)).data,
  transitionClaim: async (program: number, claim: number, body: Record<string, unknown>) =>
    (await axios.post(`${base(program)}/claims/${claim}/transition`, body)).data,

  repurchases: async (program: number) => (await axios.get<Repurchase[]>(`${base(program)}/repurchases`)).data,
  priceRepurchase: async (program: number, claim: number, body: Record<string, unknown>) =>
    (await axios.post(`${base(program)}/claims/${claim}/repurchase/price`, body)).data,
  approveRepurchase: async (program: number, id: number) =>
    (await axios.post(`${base(program)}/repurchases/${id}/approve`)).data,
  cancelRepurchase: async (program: number, id: number, reason: string) =>
    (await axios.post(`${base(program)}/repurchases/${id}/cancel`, {reason})).data,
  stageRepurchase: async (program: number, id: number, body: Record<string, unknown>) =>
    (await axios.post(`${base(program)}/repurchases/${id}/stage`, body)).data,
  advanceRepurchase: async (program: number, id: number, action: string) =>
    (await axios.post<{state: string; tradeId: number}>(`${base(program)}/repurchases/${id}/advance/${action}`)).data,
  approveAndBookRepurchase: async (program: number, id: number) =>
    (await axios.post<{state: string; tradeId: number}>(`${base(program)}/repurchases/${id}/approve-and-book`)).data,
  confirmReceipt: async (program: number, id: number, body: Record<string, unknown>) =>
    (await axios.post(`${base(program)}/repurchases/${id}/receipts`, body)).data,

  amendmentImpact: async (program: number, version: number) =>
    (await axios.get<AmendmentImpact>(`${base(program)}/agreements/${version}/impact`)).data,

  prepareStateChange: async (program: number, body: Record<string, unknown>) =>
    (await axios.post<{changeId: number}>(`${base(program)}/state-changes`, body)).data,
  applyStateChange: async (program: number, change: number) =>
    (await axios.post(`${base(program)}/state-changes/${change}/apply`)).data,
  closureChecklist: async (program: number) =>
    (await axios.get<ClosureChecklist>(`${base(program)}/closure-checklist`)).data,

  remediations: async (program: number) =>
    (await axios.get<RemediationImport[]>(`${base(program)}/remediations`)).data,
  prepareRemediation: async (program: number, body: Record<string, unknown>) =>
    (await axios.post(`${base(program)}/remediations`, body)).data,
  recordRemediation: async (program: number, id: number) =>
    (await axios.post(`${base(program)}/remediations/${id}/record`)).data,
};
